import React, { useState, useEffect } from 'react';
import { Users, UserCheck, GitBranch, TrendingUp } from 'lucide-react';
import api from '../utils/api';

const CommunityStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get('/referral/community');
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch community stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="glass-card p-6 animate-pulse">
        <div className="h-6 bg-white/10 rounded w-1/3 mb-4"></div>
        <div className="grid grid-cols-2 gap-4">
          <div className="h-24 bg-white/5 rounded-xl"></div>
          <div className="h-24 bg-white/5 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="glass-card p-6" data-testid="community-stats">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-accent to-primary rounded-xl flex items-center justify-center">
          <GitBranch className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-xl font-bold">My Community</h3>
          <p className="text-sm text-muted-foreground">Your network growth</p>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/5 rounded-xl p-4 text-center border border-white/10">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-primary" />
            </div>
          </div>
          <p className="text-3xl font-bold text-primary" data-testid="direct-referrals">
            {stats.direct_referrals}
          </p>
          <p className="text-sm text-muted-foreground">Direct Referrals</p>
        </div>
        
        <div className="bg-gradient-to-br from-accent/20 to-primary/20 rounded-xl p-4 text-center border border-accent/30">
          <div className="flex justify-center mb-2">
            <div className="w-12 h-12 bg-accent/20 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-accent" />
            </div>
          </div>
          <p className="text-3xl font-bold text-accent" data-testid="total-community">
            {stats.total_community}
          </p>
          <p className="text-sm text-muted-foreground">Total Community</p>
        </div>
      </div>

      {/* Level Breakdown */}
      {Object.keys(stats.level_breakdown).length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            Level Breakdown
          </h4>
          <div className="space-y-2">
            {Object.entries(stats.level_breakdown).map(([level, count]) => (
              <div 
                key={level} 
                className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-2"
                data-testid={`level-${level}`}
              >
                <span className="text-sm">
                  Level {level}
                  {level === '1' && <span className="text-xs text-muted-foreground ml-2">(Direct)</span>}
                </span>
                <div className="flex items-center gap-2">
                  <div 
                    className="h-2 bg-gradient-to-r from-primary to-accent rounded-full"
                    style={{ width: `${Math.min(count * 10, 100)}px` }}
                  />
                  <span className="text-sm font-semibold w-8 text-right">{count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.total_community === 0 && (
        <div className="text-center py-4 text-muted-foreground">
          <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Start referring to build your community!</p>
        </div>
      )}
    </div>
  );
};

export default CommunityStats;
